'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

// The one surface that keeps a light/dark choice (design overhaul,
// 2026-10-08). Marketing pages are light only.
const DASHBOARD_PREFIX = '/platform/app'

function setThemeCookie(value: string) {
  document.cookie = `theme=${value};path=/;max-age=31536000;samesite=lax;secure`
}

/**
 * Applies the theme rule on every client-side navigation (the layout's
 * THEME_BOOTSTRAP covers the first paint):
 * - marketing routes are always light, whatever is stored;
 * - /platform/app applies the stored choice and syncs it to the cookie the
 *   layout reads, so the dashboard's own toggle persists.
 * Without the route check, navigating from a dark dashboard back to the site
 * would leave a half-themed marketing page.
 */
export default function ThemeProvider() {
  const pathname = usePathname()

  useEffect(() => {
    const root = document.documentElement

    if (!pathname?.startsWith(DASHBOARD_PREFIX)) {
      root.setAttribute('data-theme', 'light')
      return
    }

    const stored = localStorage.getItem('theme')
    if (stored === 'light' || stored === 'dark') {
      root.setAttribute('data-theme', stored)
      setThemeCookie(stored)
    }

    // Persist toggles made on the dashboard.
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          const current = root.getAttribute('data-theme')
          if (current) setThemeCookie(current)
        }
      }
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [pathname])

  return null
}
